import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FiDownload, 
  FiZoomIn, 
  FiZoomOut, 
  FiShare2, 
  FiPrinter,
  FiInfo,
  FiTrendingUp,
  FiAward,
  FiUsers,
  FiDollarSign,
  FiBarChart2,
  FiTarget,
  FiCheckCircle
} from 'react-icons/fi';
import { 
  FaChartLine, 
  FaTree, 
  FaHandHoldingUsd, 
  FaCrown,
  FaRocket,
  FaGem
} from 'react-icons/fa';
import businessPlan from '../assets/images/business-plan.jpeg';

const BusinessPlanChart = () => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.2, 2));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = businessPlan;
    link.download = 'business-plan-chart.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Business Plan Chart',
        text: 'Check out our MLM Real Estate Business Plan',
        url: window.location.href,
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const statsCards = [
    {
      icon: <FaChartLine className="w-6 h-6" />,
      title: "Investment Plans",
      value: "3+ Plans",
      description: "Flexible investment options",
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200"
    },
    {
      icon: <FaHandHoldingUsd className="w-6 h-6" />,
      title: "Commission",
      value: "Up to 25%",
      description: "Highest in industry",
      color: "from-emerald-500 to-teal-500",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200"
    },
    {
      icon: <FaRocket className="w-6 h-6" />,
      title: "Growth Potential",
      value: "Unlimited",
      description: "No cap on earnings",
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200"
    }
  ];

  const features = [
    {
      icon: <FaCrown className="w-5 h-5" />,
      title: "Leadership Bonuses",
      description: "Earn additional rewards for building strong teams"
    },
    {
      icon: <FaTree className="w-5 h-5" />,
      title: "Binary Structure",
      description: "Balanced team growth with dual-leg commission"
    },
    {
      icon: <FiTarget className="w-5 h-5" />,
      title: "Monthly Targets",
      description: "Achieve milestones for extra incentives"
    },
    {
      icon: <FiAward className="w-5 h-5" />,
      title: "Recognition Programs",
      description: "Get recognized for your achievements"
    },
    {
      icon: <FiUsers className="w-5 h-5" />,
      title: "Team Building",
      description: "Unlimited earning through team growth"
    },
    {
      icon: <FaGem className="w-5 h-5" />,
      title: "Royalty Income",
      description: "Passive income from downline performance"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Business Plan Chart
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">MLM Real Estate Compensation Structure</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">Live Plan</span>
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart Image - Main Content */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
            >
              {/* Chart Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl">
                      <FiBarChart2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-white font-semibold text-lg">Compensation Plan</h2>
                      <p className="text-indigo-100 text-sm">Visual representation of business structure</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleZoomOut}
                      className="p-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition"
                      title="Zoom Out"
                    >
                      <FiZoomOut className="w-4 h-4 text-white" />
                    </button>
                    <button
                      onClick={handleZoomIn}
                      className="p-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition"
                      title="Zoom In"
                    >
                      <FiZoomIn className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Image Container with Zoom */}
              <div className="p-6">
                <div className="relative group overflow-hidden rounded-xl bg-gray-100">
                  <div className="overflow-auto max-h-[500px]">
                    <div style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'top left', transition: 'transform 0.3s ease' }}>
                      <img 
                        src={businessPlan} 
                        alt="Business Plan Chart" 
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  </div>
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-4">
                    <div className="text-white">
                      <p className="text-sm font-medium">MLM Real Estate Business Plan</p>
                      <p className="text-xs opacity-80">Click to zoom • Right click to save</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleDownload}
                        className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition"
                        title="Download"
                      >
                        <FiDownload className="w-4 h-4 text-white" />
                      </button>
                      <button
                        onClick={handleShare}
                        className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition"
                        title="Share"
                      >
                        <FiShare2 className="w-4 h-4 text-white" />
                      </button>
                      <button
                        onClick={handlePrint}
                        className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition"
                        title="Print"
                      >
                        <FiPrinter className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Zoom Indicator */}
                {zoomLevel !== 1 && (
                  <div className="mt-3 text-center">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      Zoom: {Math.round(zoomLevel * 100)}%
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Stats and Info Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Stats Cards */}
            {statsCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-gradient-to-r ${card.bgColor} rounded-xl p-4 border ${card.borderColor} hover:shadow-lg transition-all group`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${card.color} flex items-center justify-center mb-3 group-hover:scale-110 transition`}>
                      {React.cloneElement(card.icon, { className: "w-5 h-5 text-white" })}
                    </div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">{card.title}</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">{card.value}</p>
                    <p className="text-xs text-gray-500 mt-1">{card.description}</p>
                  </div>
                  <FiInfo className="w-4 h-4 text-gray-300" />
                </div>
              </motion.div>
            ))}

            {/* Business Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
                <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                  <FiCheckCircle className="w-4 h-4" />
                  Key Features
                </h3>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  {features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 group">
                      <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition">
                        <div className="text-indigo-600">
                          {feature.icon}
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{feature.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Call to Action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-5 text-center"
            >
              <FaChartLine className="w-8 h-8 text-white mx-auto mb-3" />
              <h3 className="text-white font-bold text-lg">Ready to Start?</h3>
              <p className="text-indigo-100 text-sm mt-1 mb-4">Join our growing network of successful partners</p>
              <button 
                onClick={handleDownload}
                className="w-full bg-white text-indigo-600 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition flex items-center justify-center gap-2"
              >
                <FiDownload className="w-4 h-4" />
                Download Plan
              </button>
            </motion.div>
          </div>
        </div>

        {/* Additional Information Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <FiTrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-800">Investment Plans</h3>
            </div>
            <p className="text-sm text-gray-600">Explore various investment opportunities and growth potential in real estate with flexible plans starting from ₹11,000.</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <FiDollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-800">Commission Structure</h3>
            </div>
            <p className="text-sm text-gray-600">Understand the detailed commission breakdown and earning potential with multiple income streams.</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <FaRocket className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-800">Growth Trajectory</h3>
            </div>
            <p className="text-sm text-gray-600">View the projected growth path and advancement opportunities from Associate to Director level.</p>
          </div>
        </motion.div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
            <FiInfo className="w-3 h-3" />
            This business plan is subject to terms and conditions. Contact support for detailed information.
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none;
          }
          body {
            background: white;
          }
          .container {
            padding: 0;
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default BusinessPlanChart;